import { useSettings } from 'core/hooks/settings/useSettings'
import { useEffect } from 'react'

/**
 * Custom hook for managing application theme based on feature flags
 *
 * This hook automatically applies the appropriate theme CSS class to the document body
 * based on the ENABLE_DRIVER_EXPERIENCE and ENABLE_GREEN_APP feature flag. It supports two themes:
 * - theme-motus: Applied when ENABLE_DRIVER_EXPERIENCE is enabled
 * - theme-green: Applied when ENABLE_GREEN_APP is enabled
 * - theme-everlance: Applied when ENABLE_DRIVER_EXPERIENCE is disabled and ENABLE_GREEN_APP is disabled
 *
 * @remarks
 * The hook cleans up previous theme classes before applying the new one to prevent
 * conflicts. The theme switch happens reactively whenever the feature flag changes.
 *
 * @returns null - This hook does not return any value
 *
 * @example
 * ```tsx
 * function App() {
 *   useTheme(); // Automatically manages theme classes
 *   return <div>...</div>;
 * }
 * ```
 */
const useTheme = () => {
  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE, ENABLE_GREEN_APP }
    }
  } = useSettings()

  /**
   * Effect hook that updates the document body theme class based on feature flag
   * Removes any existing theme classes before applying the new one
   */
  useEffect(() => {
    document.body.classList.remove(
      'theme-motus',
      'theme-everlance',
      'theme-green'
    )
    document.body.classList.add(
      'global-theme',
      ENABLE_DRIVER_EXPERIENCE
        ? 'theme-motus'
        : ENABLE_GREEN_APP
          ? 'theme-green'
          : 'theme-everlance'
    )
  }, [ENABLE_DRIVER_EXPERIENCE, ENABLE_GREEN_APP])

  return null
}

export default useTheme
